import { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { useSession } from "providers/SessionProvider";
import useCrud from "hooks/useCrud";
import { queryBuilder } from "helpers/query";
import { HISTORY_URL } from "constants/urls";
import { Client, HistoryFiltersInterface, IMovementsQuery } from "types";
import CustomGrid from "components/lists/CustomGrid";
import { FaFileDownload } from "react-icons/fa";
import { buildExcelHeader, exportToExcel } from "helpers/exportToExcel";
import HistoryFilters from "./HistoryFilters";
import { formatDateOnlyDMY } from "helpers";
import { formatMovementHistoryUnitCounts, getMovementTypeLabel } from "helpers/movement";

interface HistoryListProps {
  viewMode: "detalle" | "resumido";
}

export default function HistoryListTransfer({ viewMode }: HistoryListProps) {
  const {
    data: history,
    fetchData: getMovementHistory,
    loading: historyLoading,
  } = useCrud<any>(HISTORY_URL);

  const { stores, clients, locations, articles, userData } = useSession();

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    [],
  );
  const [filters, setFilters] = useState<HistoryFiltersInterface>({
    storeIds: [],
    locationId: undefined,
    clientIds: [],
    articleId: undefined,
    articleCode: "",
    articleDescription: "",
    lote: "",
    oc: "",
    amount_per_container: "",
    fecha_vencimiento: "",
    fecha_ingreso: "",
    fecha_desde: null as Date | null,
    fecha_hasta: null as Date | null,
  });

  const [selectedClients, setSelectedClients] = useState<Client[]>([]);

  const uniqueLotes = useMemo(
    () =>
      Array.from(
        new Set(history.map((a) => a.article_locations?.lote).filter(Boolean)),
      ),
    [history],
  );

  const uniqueOCs = useMemo(
    () =>
      Array.from(
        new Set(history.map((a) => a.article_locations?.oc).filter(Boolean)),
      ),
    [history],
  );

  const uniqueVencimientos = useMemo(
    () =>
      Array.from(
        new Set(
          history
            .map((a) => a.article_locations?.fecha_vencimiento)
            .filter(Boolean),
        ),
      ),
    [history],
  );

  const uniqueIngresos = useMemo(
    () =>
      Array.from(
        new Set(
          history
            .map((a) => a.article_locations?.fecha_ingreso)
            .filter(Boolean),
        ),
      ),
    [history],
  );

  const selectedClientIds = useMemo(
    () => selectedClients.map((client) => client.id),
    [selectedClients],
  );

  const uniqueArticles = useMemo(() => {
    const scopedArticles = selectedClientIds.length
      ? articles.filter((article) => selectedClientIds.includes(article.client_id))
      : articles;

    return Array.from(
      new Map(
        scopedArticles.map((article) => [
          `${article.client_id}-${article.code}`,
          article,
        ]),
      ).values(),
    );
  }, [articles, selectedClientIds]);

  const filteredArticles = useMemo(() => {
    if (!filters.locationId) return uniqueArticles;

    const articlesFromHistory = history
      .filter((h) => h?.article_locations?.location_id === filters.locationId)
      .map((h) => h.article_locations?.article)
      .filter(Boolean);

    return Array.from(
      new Map(articlesFromHistory.map((a) => [a.id, a])).values(),
    );
  }, [history, filters.locationId, uniqueArticles]);

  const availableLocationsForArticle = useMemo(() => {
    if (!filters.articleId) return locations;

    const locIds = history
      .filter((h) => h.article_id === filters.articleId)
      .map((h) => h.article_locations?.location_id);

    const locSet = new Set(locIds);

    return locations.filter((l) => locSet.has(l.id));
  }, [history, filters.articleId, locations]);

  const availableStoresForArticle = useMemo(() => {
    if (!filters.articleId) return stores;

    const storeIds = history
      .filter((h) => h.article_id === filters.articleId)
      .map((h) => h.article_locations?.location?.store_id);
    const storeSet = new Set(storeIds);

    return stores.filter((s) => storeSet.has(s.id));
  }, [history, filters.articleId, stores]);

  const uniqueFechasMovimiento = useMemo(
    () =>
      Array.from(
        new Set(
          history.map((h) => h.movement?.date?.split("T")[0]).filter(Boolean),
        ),
      ),
    [history],
  );

  const handleSearch = async () => {
    const query = queryBuilder({
      storeIds: filters.storeIds,
      locationId: filters.locationId,
      clientIds: filters.clientIds,
      articleId: filters.articleId,
      lote: filters.lote || undefined,
      oc: filters.oc || undefined,
      amount_per_container: filters.amount_per_container || undefined,
      articleDescription: filters.articleDescription.trim() || undefined,
      fecha_vencimiento: filters.fecha_vencimiento || undefined,
      fecha_ingreso: filters.fecha_ingreso || undefined,
      fecha_desde: filters.fecha_desde
        ? filters.fecha_desde.toISOString()
        : undefined,
      fecha_hasta: filters.fecha_hasta
        ? filters.fecha_hasta.toISOString()
        : undefined,
      withTransfers: true,
    } as IMovementsQuery);

    await getMovementHistory(query, true);
  };

  useEffect(() => {
    handleSearch();
  }, [filters]);

  const handleExportToExcel = () => {
    const { headerRows } = buildExcelHeader(clients, filters, userData);

    const columns: Record<string, (a: any) => any> = {
      "Nº Movimiento": (a) => a.movement?.id ?? "-",

      "Tipo de Movimiento": (a) =>
        getMovementTypeLabel(a.movement?.movement_type_id, {
          isTransferFrom: a.isTransferFrom,
          isTransferTo: a.isTransferTo,
        }),

      "Fecha de Movimiento": (a) =>
        a.movement?.date ? formatDateOnlyDMY(a.movement.date) : "-",

      "Código de Artículo": (a) => a.article_locations?.article?.code ?? "-",

      Descripción: (a) => a.article_locations?.article?.description ?? "-",

      Cliente: (a) => a.article_locations?.article?.client?.code ?? "-",

      "Doc. del Cliente": (a) => a.movement?.client_document || "-",

      "Cliente Asociado": (a) => a.movement?.associated_client || "-",

      Ubicación: (a) => a.article_locations?.location?.code ?? "-",
    };

    if (filterVisibility.show_lote) {
      columns["Lote"] = (a) => a.article_locations?.lote ?? "-";
    }

    columns["Fardo/Paquete"] = (a) =>
      formatMovementHistoryUnitCounts(
        a.movement?.movement_type_id,
        a.article_locations,
      );

    if (filterVisibility.show_oc) {
      columns["OC"] = (a) => a.article_locations?.oc ?? "-";
    }

    if (filterVisibility.show_fecha_vencimiento) {
      columns["Fecha Vencimiento"] = (a) =>
        a.article_locations?.fecha_vencimiento ?? "-";
    }

    columns["Fecha Ingreso"] = (a) =>
      a.article_locations?.fecha_ingreso
        ? formatDateOnlyDMY(a.article_locations.fecha_ingreso)
        : "-";

    exportToExcel({
      data: history,
      columns,
      sheetName: "Historial",
      fileNamePrefix: "Historial_Movimientos",
      headerRows,
    });
  };

  const filterVisibility = useMemo(() => {
    if (selectedClients.length === 0) {
      return {
        show_lote: true,
        show_oc: true,
        show_fecha_vencimiento: true,
      };
    }

    return {
      show_lote: selectedClients.some((c) => c.show_lote),
      show_oc: selectedClients.some((c) => c.show_oc),
      show_fecha_vencimiento: selectedClients.some(
        (c) => c.show_fecha_vencimiento,
      ),
    };
  }, [selectedClients]);

  const structure = useMemo(
    () => ({
      name: "movementHistory",
      labelName: "Historial de Movimientos",
      fields: [
        {
          field: "movement_id",
          headerName: "Nº Movimiento",
          flex: 1,
          valueGetter: (p: any) => p.row.movement?.id ?? "-",
        },
        {
          field: "tipo_movimiento",
          headerName: "Tipo de Movimiento",
          flex: 1,
          valueGetter: (p: any) =>
            getMovementTypeLabel(p.row.movement?.movement_type_id, {
              isTransferFrom: p.row.isTransferFrom,
              isTransferTo: p.row.isTransferTo,
            }),
        },
        {
          field: "fecha",
          headerName: "Fecha de Movimiento",
          flex: 1,
          type: "date",
          valueGetter: (p: any) =>
            p.row.movement?.date
              ? new Date(p.row.movement.sort_date ?? p.row.movement.date)
              : null,

          valueFormatter: (params: any) => {
            if (!params.value) return "-";
            return formatDateOnlyDMY((params.value as Date).toISOString());
          },
        },
        {
          field: "codigo",
          headerName: "Código de Artículo",
          flex: 1,
          valueGetter: (p: any) =>
            p.row.article_locations?.article?.code ?? "-",
        },
        {
          field: "descripcion",
          headerName: "Descripción",
          flex: 1.5,
          valueGetter: (p: any) =>
            p.row.article_locations?.article?.description ?? "-",
        },
        {
          field: "cliente",
          headerName: "Cliente",
          flex: 1,
          valueGetter: (p: any) =>
            p.row.article_locations?.article?.client?.code ?? "-",
        },
        {
          field: "client_document",
          headerName: "Doc. del cliente",
          flex: 1,
          valueGetter: (p: any) => p.row.movement?.client_document || "-",
        },
        {
          field: "associated_client",
          headerName: "Cliente Asociado",
          flex: 1.2,
          valueGetter: (p: any) => p.row.movement?.associated_client || "-",
        },
        {
          field: "ubicacion",
          headerName: "Ubicación",
          flex: 1,
          valueGetter: (p: any) =>
            p.row.article_locations?.location?.code ?? "-",
        },
        filterVisibility.show_lote && {
          field: "lote",
          headerName: "Lote",
          flex: 1,
          valueGetter: (p: any) => p.row.article_locations?.lote ?? "-",
        },
        {
          field: "fardo_paquete",
          headerName: "Fardo/Paquete",
          flex: 1.4,
          valueGetter: (p: any) =>
            formatMovementHistoryUnitCounts(
              p.row.movement?.movement_type_id,
              p.row.article_locations,
            ),
        },
        filterVisibility.show_oc && {
          field: "oc",
          headerName: "OC",
          flex: 1,
          valueGetter: (p: any) => p.row.article_locations?.oc ?? "-",
        },
        filterVisibility.show_fecha_vencimiento && {
          field: "fecha_vencimiento",
          headerName: "Fecha Vencimiento",
          flex: 1,
          valueGetter: (p: any) =>
            p.row.article_locations?.fecha_vencimiento ?? "-",
        },
        {
          field: "fecha_ingreso",
          headerName: "Fecha Ingreso",
          flex: 1,
          valueGetter: (p: any) =>
            p.row.article_locations?.fecha_ingreso
              ? formatDateOnlyDMY(p.row.article_locations.fecha_ingreso)
              : "-",
        },
      ].filter(Boolean) as GridColDef[],
    }),
    [filterVisibility],
  );

  useEffect(() => {
    setFilters({
      storeIds: [],
      locationId: undefined,
      clientIds: [],
      articleId: undefined,
      lote: "",
      oc: "",
      amount_per_container: "",
      articleDescription: "",
      fecha_vencimiento: "",
      fecha_ingreso: "",
      fecha_desde: null as Date | null,
      fecha_hasta: null as Date | null,
    });

    setSelectedClients([]);
  }, [viewMode]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper elevation={4} sx={{ p: 2 }}>
          <Typography variant="h6">Filtros</Typography>
          <HistoryFilters
            key={viewMode}
            viewMode={viewMode}
            filters={filters}
            setFilters={setFilters}
            selectedClients={selectedClients}
            setSelectedClients={setSelectedClients}
            uniqueValues={{
              lotes: uniqueLotes,
              ocs: uniqueOCs,
              vencimientos: uniqueVencimientos,
              ingresos: uniqueIngresos,
              movimientoFechas: uniqueFechasMovimiento,
            }}
            filteredArticles={filteredArticles}
            availableStores={availableStoresForArticle}
            availableLocations={availableLocationsForArticle}
            filterVisibility={filterVisibility}
            clients={clients}
            stores={stores}
            uniqueArticles={uniqueArticles}
            loading={historyLoading}
          />

          <Grid container justifyContent="flex-end" mb={2}>
            <Button
              variant="contained"
              color="success"
              startIcon={<FaFileDownload />}
              onClick={handleExportToExcel}
              disabled={history.length === 0}
            >
              Exportar Excel
            </Button>
          </Grid>

          {historyLoading ? (
            <Grid item xs={12} display="flex" justifyContent="center" my={6}>
              <CircularProgress />
            </Grid>
          ) : (
            <CustomGrid<any>
              structure={structure}
              rows={history}
              onEdit={() => {}}
              selectionModel={selectionModel}
              onRowSelection={setSelectionModel}
              checkboxSelection={false}
              initialState={{
                sorting: {
                  sortModel: [{ field: "fecha", sort: "desc" }],
                },
              }}
            />
          )}

          {!historyLoading && history.length === 0 && (
            <Alert severity="info">
              <Typography variant="body2">
                No hay resultados disponibles
              </Typography>
            </Alert>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}
